import { useQuery } from '@tanstack/react-query';

import { api } from './api';

const TEN_MIN = 10 * 60 * 1000;

export function useCategories(lang) {
  return useQuery({
    queryKey: ['categories', lang],
    queryFn: () => api.get('/categories', { params: { lang } }).then((r) => r.data),
    staleTime: TEN_MIN,
  });
}

export function useCollections(lang) {
  return useQuery({
    queryKey: ['collections', lang],
    queryFn: () => api.get('/collections', { params: { lang } }).then((r) => r.data),
    staleTime: TEN_MIN,
  });
}

export function useCategory(slug, lang) {
  return useQuery({
    queryKey: ['category', slug, lang],
    queryFn: () => api.get(`/categories/${slug}`, { params: { lang } }).then((r) => r.data),
    staleTime: TEN_MIN,
    retry: (count, err) => err?.response?.status !== 404 && count < 2,
  });
}

/** Senza `categorySlug` i facet sono globali (Collezioni); con lo slug sono
 *  ristretti a quella categoria (pagina categoria). */
export function useFacets(categorySlug, lang = 'it') {
  return useQuery({
    queryKey: ['facets', categorySlug ?? null, lang],
    queryFn: () =>
      api.get('/facets', { params: { category_slug: categorySlug, lang } }).then((r) => r.data),
    staleTime: TEN_MIN,
  });
}

/**
 * Prodotti filtrati e paginati. `params` è l'oggetto dei filtri attivi:
 * le chiavi con valore falsy vengono scartate, così la queryKey resta
 * stabile e la cache non si frammenta su `undefined` vs assente.
 */
export function useProducts(params) {
  const clean = Object.fromEntries(
    Object.entries(params).filter(([, v]) => v !== undefined && v !== '' && v !== null),
  );
  return useQuery({
    queryKey: ['products', clean],
    queryFn: () => api.get('/products', { params: clean }).then((r) => r.data),
    staleTime: 2 * 60 * 1000,
    // Tiene i risultati precedenti mentre cambia filtro o pagina, ma NON tra
    // categorie diverse: la route /catalogo/:slug riusa lo stesso componente,
    // quindi senza questo controllo la nuova categoria mostrerebbe le tile e
    // il conteggio di quella vecchia finche' la fetch non risponde.
    placeholderData: (prev, prevQuery) => {
      const before = prevQuery?.queryKey[1] ?? {};
      const same =
        before.category_slug === clean.category_slug && before.macro_slug === clean.macro_slug;
      return same ? prev : undefined;
    },
  });
}
